import React, { createContext, useContext, useState, useEffect } from 'react'
import { 
  auth, 
  signIn, 
  signUp, 
  logOut, 
  resetPassword,
  getDocument,
  createDocument,
  updateDocument,
  signInWithGoogle,
  deleteAuthUser
} from '../services/firebase'
import { onAuthStateChanged } from 'firebase/auth'
import { initializeNotifications } from '../services/notifications'
import {
  isUserAccountDeleted,
  isEmailDeleted,
  clearDeletedEmailRecord,
  releaseRemovedEmailForRegistration
} from '../utils/accountDeletion'
import toast from 'react-hot-toast'
import i18n from '../translations/i18n'

const AuthContext = createContext()

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [userData, setUserData] = useState(null)
  const [loading, setLoading] = useState(true)

  const rejectDeletedAccount = async (firebaseUser, showToast = true) => {
    const isDeleted = await isUserAccountDeleted(firebaseUser?.uid)
    if (!isDeleted) {
      return false
    }

    await logOut()
    setUser(null)
    setUserData(null)

    if (showToast) {
      toast.error(i18n.t('auth.accountRemovedRegister'))
    }

    return true
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          if (await rejectDeletedAccount(user, true)) {
            setLoading(false)
            return
          }

          setUser(user)
          const userDoc = await getDocument('users', user.uid)
          if (userDoc.exists()) {
            setUserData({ id: userDoc.id, ...userDoc.data() })
            let lastNotificationKey = null
            let lastNotificationTime = 0

            initializeNotifications(user.uid, (payload) => {
              const now = Date.now()
              const notificationKey = `${payload.notification?.body}-${payload.data?.createdAt}-${payload.data?.type}`
              
              // Skip if same notification shown within 2 seconds (prevent duplicates)
              if (now - lastNotificationTime < 2000 && lastNotificationKey === notificationKey) {
                console.log('Skipping duplicate notification:', notificationKey)
                return
              }
              lastNotificationKey = notificationKey
              lastNotificationTime = now
              
              if (payload.notification) {
                toast.success(payload.notification.body)
              }
            })
          } else {
            // If user document doesn't exist, create it
            const defaultUserData = {
              uid: user.uid,
              email: user.email,
              firstName: user.displayName?.split(' ')[0] || '',
              lastName: user.displayName?.split(' ')[1] || '',
              age: 0,
              city: '',
              phone: '',
              sports: 'calcio',
              role: '',
              accessRole: 'user',
              profileImage: user.photoURL || '',
              coverImage: '',
              points: 60,
              rating: 0,
              matchesPlayed: 0,
              goals: 0,
              assists: 0,
              wins: 0,
              losses: 0,
              draws: 0,
              followers: [],
              following: [],
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString()
            }
            await clearDeletedEmailRecord(user.email)
            await createDocument('users', user.uid, defaultUserData)
            setUserData({ id: user.uid, ...defaultUserData })
          }
        } catch (error) {
          console.error('Error fetching user data:', error)
          toast.error(i18n.t('auth.errorLoadingUserData'))
        }
      } else {
        setUser(null)
        setUserData(null)
      }
      setLoading(false)
    })
    return unsubscribe
  }, [])

  const login = async (email, password) => {
    try {
      const result = await signIn(email, password)
      if (await rejectDeletedAccount(result.user, false)) {
        const message = i18n.t('auth.accountRemovedRegister')
        toast.error(message)
        return { success: false, error: message }
      }

      toast.success(i18n.t('auth.welcome'))
      return { success: true, user: result.user }
    } catch (error) {
      console.error('Login error:', error)
      let message = i18n.t('auth.loginFailed')
      if (error.code === 'auth/user-not-found' && await isEmailDeleted(email)) {
        message = i18n.t('auth.accountRemovedRegister')
      } else if (error.code === 'auth/user-not-found') {
        message = i18n.t('auth.userNotFound')
      }
      if (error.code === 'auth/wrong-password') message = i18n.t('auth.wrongPassword')
      if (error.code === 'auth/invalid-email') message = i18n.t('auth.invalidEmail')
      if (error.code === 'auth/too-many-requests') message = i18n.t('auth.tooManyRequests')
      toast.error(message)
      return { success: false, error: message }
    }
  }

  const googleSignInWithUser = async (user) => {
    const rawDisplayName = String(user.displayName || '').trim()
    const nameParts = rawDisplayName.split(/\s+/).filter(Boolean)
    const fallbackFirstName = user.email ? user.email.split('@')[0] : ''
    const firstName = nameParts[0] || fallbackFirstName || 'Player'
    const lastName = nameParts.slice(1).join(' ')

    const userDoc = await getDocument('users', user.uid)
    if (!userDoc.exists()) {
      const defaultUserData = {
        uid: user.uid,
        email: user.email,
        firstName,
        lastName,
        age: 0,
        city: '',
        phone: '',
        sports: 'calcio',
        role: '',
        accessRole: 'user',
        profileImage: user.photoURL || '',
        coverImage: '',
        points: 60,
        rating: 0,
        matchesPlayed: 0,
        goals: 0,
        assists: 0,
        wins: 0,
        losses: 0,
        draws: 0,
        followers: [],
        following: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }
      await clearDeletedEmailRecord(user.email)
      await createDocument('users', user.uid, defaultUserData)
      setUserData({ id: user.uid, ...defaultUserData })
    } else {
      setUserData({ id: userDoc.id, ...userDoc.data() })
    }

    toast.success(i18n.t('auth.welcome'))
    return { success: true, user }
  }

  const googleSignIn = async () => {
    try {
      const result = await signInWithGoogle()
      const user = result.user

      if (await rejectDeletedAccount(user, false)) {
        if (user.email && (await isEmailDeleted(user.email))) {
          try {
            await releaseRemovedEmailForRegistration(user.email)
            const retryResult = await signInWithGoogle()
            return await googleSignInWithUser(retryResult.user)
          } catch (releaseError) {
            console.error('Google re-registration release failed:', releaseError)
            const message = i18n.t('auth.releaseEmailFailed')
            toast.error(message)
            return { success: false, error: message }
          }
        }

        const message = i18n.t('auth.accountRemovedRegister')
        toast.error(message)
        return { success: false, error: message }
      }

      return await googleSignInWithUser(user)
    } catch (error) {
      console.error('Google Sign-In error:', error)
      let message = i18n.t('auth.googleSignInFailed')
      let errorCode = error.code

      if (error.code === 'auth/popup-closed-by-user') {
        message = i18n.t('auth.popupClosed')
        errorCode = 'popup-closed'
      } else if (error.code === 'auth/popup-blocked') {
        message = i18n.t('auth.popupBlocked')
        errorCode = 'popup-blocked'
      } else if (error.code === 'auth/account-exists-with-different-credential') {
        message = i18n.t('auth.accountExistsDifferentCredential')
        errorCode = 'account-exists'
      } else if (error.code === 'auth/operation-not-allowed') {
        message = 'Google Sign-In is not enabled. Please contact support.'
        errorCode = 'operation-not-allowed'
      }

      toast.error(message, { duration: 5000 })
      return { success: false, error: errorCode, message }
    }
  }

  const forgotPassword = async (email) => {
    try {
      await resetPassword(email)
      toast.success(i18n.t('auth.passwordResetSent'))
      return { success: true }
    } catch (error) {
      console.error('Reset password error:', error)
      let message = i18n.t('auth.passwordResetFailed')
      if (error.code === 'auth/user-not-found') {
        message = i18n.t('auth.emailNotFound')
      } else if (error.code === 'auth/invalid-email') {
        message = i18n.t('auth.invalidEmail')
      }
      toast.error(message)
      return { success: false, error: message }
    }
  }

  const clearRemovedAccountWithPreviousPassword = async (email, previousPassword) => {
    const signInResult = await signIn(email, previousPassword)
    const oldUser = signInResult.user
    const wasRemoved = await isUserAccountDeleted(oldUser.uid)

    if (!wasRemoved) {
      await logOut()
      const error = new Error('EMAIL_IN_USE')
      error.code = 'auth/email-already-in-use'
      throw error
    }

    await deleteAuthUser(oldUser)
    await logOut()
  }

  const completeRegistration = async (firebaseUser, userInfo) => {
    await clearDeletedEmailRecord(firebaseUser.email || userInfo.email)

    const newUserData = {
      uid: firebaseUser.uid,
      email: firebaseUser.email,
      firstName: userInfo.firstName,
      lastName: userInfo.lastName,
      age: parseInt(userInfo.age, 10),
      city: userInfo.city,
      phone: userInfo.phone,
      sports: userInfo.sports,
      role: userInfo.role,
      accessRole: 'user',
      profileImage: '',
      coverImage: '',
      points: 60,
      rating: 0,
      matchesPlayed: 0,
      goals: 0,
      assists: 0,
      wins: 0,
      losses: 0,
      draws: 0,
      followers: [],
      following: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    }

    await createDocument('users', firebaseUser.uid, newUserData)
    setUserData({ id: firebaseUser.uid, ...newUserData })
    toast.success(i18n.t('auth.registeredSuccessfully'))
    return { success: true, user: firebaseUser }
  }

  const register = async (email, password, userInfo) => {
    try {
      let signUpResult

      try {
        signUpResult = await signUp(email, password)
      } catch (signupError) {
        if (signupError?.code !== 'auth/email-already-in-use') {
          throw signupError
        }

        const wasRemovedByAdmin = await isEmailDeleted(email)
        if (!wasRemovedByAdmin) {
          throw signupError
        }

        if (!userInfo?.previousPassword) {
          return { success: false, needsPreviousPassword: true }
        }

        try {
          await clearRemovedAccountWithPreviousPassword(email, userInfo.previousPassword)
        } catch (clearError) {
          if (
            clearError?.code === 'auth/wrong-password' ||
            clearError?.code === 'auth/invalid-credential' ||
            clearError?.code === 'auth/user-not-found'
          ) {
            const message = i18n.t('auth.previousPasswordWrong')
            toast.error(message)
            return { success: false, needsPreviousPassword: true, error: message }
          }
          throw clearError
        }

        signUpResult = await signUp(email, password)
      }

      return await completeRegistration(signUpResult.user, { ...userInfo, email })
    } catch (error) {
      console.error('Register error:', error)
      let message = i18n.t('auth.registrationFailed')

      if (error.code === 'auth/email-already-in-use') {
        message = i18n.t('auth.emailAlreadyInUse')
      }
      if (error.code === 'auth/weak-password') message = i18n.t('auth.weakPassword')
      if (error.code === 'auth/invalid-email') message = i18n.t('auth.invalidEmail')

      toast.error(message)
      return { success: false, error: message }
    }
  }

  const logout = async () => {
    try {
      await logOut()
      localStorage.removeItem('darkMode')
      toast.success(i18n.t('auth.loggedOutSuccessfully'))
      return { success: true }
    } catch (error) {
      console.error('Logout error:', error)
      toast.error(i18n.t('auth.logoutError'))
      return { success: false }
    }
  }

  const updateUserData = async (data) => {
    try {
      await updateDocument('users', user.uid, { ...data, updatedAt: new Date().toISOString() })
      setUserData(prev => ({ ...prev, ...data }))
      toast.success(i18n.t('auth.profileUpdated'))
      return { success: true }
    } catch (error) {
      console.error('Update user error:', error)
      toast.error(i18n.t('auth.profileUpdateError'))
      return { success: false }
    }
  }

  const value = {
    user,
    userData,
    loading,
    login,
    googleSignIn,
    forgotPassword,
    register,
    logout,
    updateUserData,
    setUserData
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}